<script setup lang="ts">
import { computed, ComputedRef, ref, useTemplateRef, watch } from 'vue'

import { MultiSelect, SuccessBadge } from '@collector/shared-ui'
import { Incident } from '@collector/sportsapi-client'
import { useAuthInfo } from '@desktop/globalState/userAuth/useAuthInfo'
import { useTeamSides } from '@desktop/views/Relation/composables'
import {
  useEventIncidentsQueue,
  useIncidentPlayers,
} from '@desktop/views/Relation/Sports/composables'
import {
  getSubparticipantProperties,
  SubParticipantType,
} from '@desktop/views/Relation/Sports/configuration'
import {
  IncidentPlayerOption,
  IncidentPlayerOptionValue,
} from '@desktop/views/Relation/types'

import { isPlayerSelectOptionMatched } from '../../../../../utils'
import {
  getSubParticipantIdValue,
  getSubParticipantNameValue,
  getSubParticipantPathValue,
  getSubparticipantValue,
  isSubParticipantOption,
  SubParticipantValue,
} from './utils'

// props
interface Props {
  disabled: boolean
  incident: Incident
  label: string
  options: IncidentPlayerOption[]
  required: boolean
  type: SubParticipantType
  keepSelectedOption?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  keepSelectedOption: false,
})

// composables
const eventIncidentsQueue = useEventIncidentsQueue()
const { homePlayersOptions, awayPlayersOptions } = useIncidentPlayers()
const { authInfo } = useAuthInfo()
const { getIncidentTeamSide } = useTeamSides()

// data
const subParticipantProperties = getSubparticipantProperties(props.type)

// state
const subParticipantValue = ref(
  getSubParticipantPathValue(props.incident, subParticipantProperties),
)

const successBadge = useTemplateRef('successBadge')

// computed
type OptionWithoutTeamId = Omit<IncidentPlayerOption, 'value'> & {
  value: Omit<IncidentPlayerOptionValue, 'teamId' | 'lineupId'>
}

type OptionsWithoutTeamId = OptionWithoutTeamId[]

const optionsWithoutTeamId: ComputedRef<OptionsWithoutTeamId> = computed(() => {
  const options = props.options.map((option) => {
    const { teamId, lineupId, ...rest } = option.value
    return { ...option, value: rest }
  })

  if (props.keepSelectedOption) {
    appendCurrentSelectedOption(options)
  }

  return options
})

// methods
/**
 * If `subParticipant` is selected (i.e. already assigned in incident properties)
 * but not present in the available select options (due to being restricted from
 * multiple assignments across incidents), manually append it to the options list
 * to preserve the current selection and display player name in the select.
 */
function appendCurrentSelectedOption(options: OptionsWithoutTeamId): void {
  if (!subParticipantValue.value) {
    return
  }

  const isSubParticipantInOptions = options.some((option) =>
    isSubParticipantOption(subParticipantValue.value, option.value),
  )

  if (!isSubParticipantInOptions) {
    const subParticipantOption = getSubParticipantSelectOption()

    if (subParticipantOption) {
      options.unshift(subParticipantOption)
    }
  }
}

/**
 * Locate the `subParticipant` within all players for the team,
 * then convert it into a valid select option.
 */
function getSubParticipantSelectOption(): OptionWithoutTeamId | null {
  const teamSide = getIncidentTeamSide(props.incident)

  if (!teamSide) {
    return null
  }

  const playerOptions =
    teamSide === 'home' ? homePlayersOptions.value : awayPlayersOptions.value

  const playerOption = playerOptions.find((player) =>
    isSubParticipantOption(subParticipantValue.value, player.value),
  )

  if (!playerOption) {
    return null
  }

  const metadata = playerOption.metadata

  return {
    name: `${metadata.shirtNr} ${playerOption.value.shortName}`.trim(),
    value: {
      shortName: playerOption.value.shortName ?? '',
      id: playerOption.value.id ?? null,
    },
    metadata,
  }
}

function updatePlayer(value: SubParticipantValue): void {
  const payload = {
    [subParticipantProperties.propertyId]: value?.id ?? null,
    [subParticipantProperties.propertyName]: value?.shortName ?? null,
    participant_team_id: props.incident.participant_team_id,
  }

  eventIncidentsQueue.updateIncident(payload, props.incident)

  subParticipantValue.value = value
}

// init
watch(
  [
    () =>
      getSubParticipantNameValue(
        props.incident,
        subParticipantProperties.propertyName,
      ),
    () =>
      getSubParticipantIdValue(
        props.incident,
        subParticipantProperties.propertyId,
      ),
  ],
  ([newName, newId]) => {
    if (
      props.incident.action === 'update' &&
      props.incident.source_id === authInfo.value?.id // incident was modified by the current user
    ) {
      successBadge.value?.show()
    }

    subParticipantValue.value = getSubparticipantValue(newName, newId)
  },
)

// Watcher to reset subParticipantValue when optionsWithoutTeamId changes
watch(optionsWithoutTeamId, () => {
  if (subParticipantValue.value) {
    const isSubParticipantSelected = optionsWithoutTeamId.value.find((option) =>
      isSubParticipantOption(subParticipantValue.value!, option.value),
    )

    if (!isSubParticipantSelected) {
      subParticipantValue.value = undefined
    }
  }
})
</script>

<template>
  <MultiSelect
    class="gap-x-2"
    :options="optionsWithoutTeamId"
    labelClass="min-w-13 max-w-13 justify-end"
    :disabled
    :required
    withClear
    withFilter
    withOptionsFullWidth
    :filterPredicate="isPlayerSelectOptionMatched"
    :modelValue="subParticipantValue"
    @update:modelValue="(value) => updatePlayer(value as SubParticipantValue)"
  >
    <template #default>
      {{ label }}
    </template>
    <template #option="{ option }">
      <div class="whitespace-nowrap">
        {{ option.value.shortName }}
      </div>
      <div class="text-2xs">
        {{ option.metadata.position }}
        <span
          v-if="option.metadata.shirtNr"
          class="font-bold"
        >
          {{ option.metadata.shirtNr }}
        </span>
      </div>
    </template>
    <template #trigger>
      <SuccessBadge
        ref="successBadge"
        title="player updated"
      />
    </template>
  </MultiSelect>
</template>
